import { WEAPON_ICONS } from '../data/assets/visualAssets';
import { bindUIFocus } from './UIButton';
import { UITheme } from './UITheme';
import * as Phaser from 'phaser';
import { LocalizationManager } from '../managers/LocalizationManager';
import { BossHealthBar } from './BossHealthBar';
import { UpgradeInventorySnapshot } from '../systems/UpgradeSystem';
import { createLogger } from '../utils/Logger';
import { isBossLookingPayload, BossEncounterEventMetadata } from '../systems/BossEventPayloads';

/**
 * Интерфейс для системы опыта
 */
interface ExperienceSystem {
  currentLevel: number;
  currentXP: number;
  xpForNextLevel: number;
}


/**
 * Класс HUD (Heads-Up Display) - отображает информацию об игроке в реальном времени
 * @extends Phaser.GameObjects.Container
 */
export class HUD extends Phaser.GameObjects.Container {

  // Элементы UI
  private healthValue?: Phaser.GameObjects.Text;
  private xpValue?: Phaser.GameObjects.Text;
  private waveText?: Phaser.GameObjects.Text;
  private wave = -1;
  private healthBar: Phaser.GameObjects.Graphics | null = null;
  private xpBar: Phaser.GameObjects.Graphics | null = null;
  private levelText: Phaser.GameObjects.Text | null = null;
  // Stats Text
  private statsText: Phaser.GameObjects.Text | null = null;
  // Timer Text
  private timerText: Phaser.GameObjects.Text | null = null;
  private inventoryContainer: Phaser.GameObjects.Container | null = null;
  private weaponSlots: InventorySlotView[] = [];
  private passiveSlots: InventorySlotView[] = [];

  // Boss Health Bar
  private bossHealthBar: BossHealthBar | null = null;
  private pauseButton: Phaser.GameObjects.Container | null = null;
  private readonly eventUnsubscribers: Array<() => void> = [];
  private isInitialized = false;
  private isDisposed = false;

  // Параметры UI
  private readonly barWidth: number = 170;
  private readonly barHeight: number = 20;
  private readonly barPadding: number = 10;
  private readonly inventorySlotSize: number = 38;
  private readonly inventorySlotGap: number = 6;
  private readonly logger = createLogger('HUD');

  /**
   * Инициализирует HUD с игроком и системой опыта
   * @param scene - Сцена Phaser, к которой привязывается HUD
   * @param player - Ссылка на игрока
   * @param experienceSystem - Система опыта
   */
  init(
    scene: Phaser.Scene,
    _player: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody,
    _experienceSystem: ExperienceSystem
  ): void {
    if (this.isInitialized) {
      this.logger.warn('init:already-initialized');
      this.dispose();
    }
    this.isDisposed = false;

    // Создаем элементы UI
    this.createHealthBar(scene);
    this.createXPBar(scene);
    this.createLevelText(scene);
    this.createTimerText(scene);
    this.createStatsText(scene);
    this.createInventorySlots(scene);
    this.createPauseButton(scene);
    const style = {fontFamily:UITheme.body,fontSize:'16px',color:UITheme.text,stroke:'#101f1c',strokeThickness:3};
    this.healthValue = scene.add.text(16,10,'',style);
    this.xpValue = scene.add.text(16,40,'',style);
    this.waveText = scene.add.text(10,90,'',style);
    this.add([this.healthValue,this.xpValue,this.waveText]);
    for (const [key,y] of [['ui_health',20],['ui_experience',50]] as const) {
      if(scene.textures.exists(key)) this.add(scene.add.image(198,y,key).setDisplaySize(24,24));
    }

    // Boss Health Bar
    this.bossHealthBar = new BossHealthBar(scene);
    this.add(this.bossHealthBar);

    // Подписываемся на события
    if ('eventBus' in scene) {
      const bus = (scene as any).eventBus;
      this.bindBusEvent(bus, 'xp:gained', (event: any) => {
        const { current, total } = event.data;
        this.updateXP(current, total);
      });

      this.bindBusEvent(bus, 'level:up', (event: any) => {
        const { level } = event.data;
        if (this.levelText) {
          const loc = LocalizationManager.getInstance();
          this.levelText.setText(`${loc.getText('ui.level')} ${level}`);
        }
      });

      // Boss Events
      this.bindBusEvent(bus, 'boss:spawned', (event: any) => {
        const { boss, bossEncounter } = event.data as { boss?: any; bossEncounter?: BossEncounterEventMetadata };
        let maxHealth = 1000;
        if (bossEncounter?.resolvedStats?.maxHealth && Number.isFinite(bossEncounter.resolvedStats.maxHealth)) {
          maxHealth = bossEncounter.resolvedStats.maxHealth;
        } else if (boss && typeof boss.getMaxHealth === 'function') {
          maxHealth = boss.getMaxHealth();
        } else if (boss && boss.maxHealth) { // Fallback if property
          maxHealth = boss.maxHealth;
        }

        const loc = LocalizationManager.getInstance();
        const localizedDisplayName = bossEncounter?.displayNameKey ? loc.getText(bossEncounter.displayNameKey) : undefined;
        const localizedEnemyName = bossEncounter?.enemyNameKey ? loc.getText(bossEncounter.enemyNameKey) : undefined;
        const hasLocalizedDisplayName = Boolean(localizedDisplayName && localizedDisplayName !== bossEncounter?.displayNameKey);
        const hasLocalizedEnemyName = Boolean(localizedEnemyName && localizedEnemyName !== bossEncounter?.enemyNameKey);
        const bossName = hasLocalizedDisplayName
          ? localizedDisplayName!
          : (hasLocalizedEnemyName
              ? localizedEnemyName!
              : (bossEncounter?.displayName
                  || bossEncounter?.enemyName
                  || bossEncounter?.enemyId
                  || loc.getText('ui.boss')));

        this.logger.debug('boss:spawned-ui-update', {
          bossName,
          maxHealth,
          encounterId: bossEncounter?.encounterId
        });
        this.bossHealthBar?.show(bossName, maxHealth);
      });

      this.bindBusEvent(bus, 'enemy:hit', (event: any) => {
        const { id, currentHealth, bossEncounter } = event.data as { id?: string; currentHealth?: number; bossEncounter?: BossEncounterEventMetadata };
        if (bossEncounter) {
          if (Number.isFinite(currentHealth)) {
            this.bossHealthBar?.updateHealth(currentHealth as number);
          }
          return;
        }

        if (isBossLookingPayload(event.data)) {
          this.logger.warn('enemy:hit-missing-boss-encounter-metadata', { id });
        }
      });

      this.bindBusEvent(bus, 'enemy:killed', (event: any) => {
        const { id, bossEncounter } = event.data as { id?: string; bossEncounter?: BossEncounterEventMetadata };
        if (bossEncounter) {
          this.bossHealthBar?.hide();
          return;
        }

        if (isBossLookingPayload(event.data)) {
          this.logger.warn('enemy:killed-missing-boss-encounter-metadata', { id });
        }
      });
    }

    // Позиционируем элементы
    this.positionElements();
    this.isInitialized = true;
  }

  /**
   * Обновляет отображение характеристик игрока
   * @param stats - Объект с характеристиками
   */
  updateStats(stats: any): void {
    if (this.statsText && stats) {
      const loc = LocalizationManager.getInstance();
      const dmg = (stats.get('damageMultiplier') * 100).toFixed(0);
      const spd = stats.get('moveSpeed').toFixed(0);
      const hp = stats.get('maxHealth').toFixed(0);
      const cooldown = (stats.get('cooldownMultiplier') * 100).toFixed(0);
      const pickup = stats.get('pickupRange').toFixed(0);

      const dmgLabel = loc.getText('ui.damage');
      const spdLabel = loc.getText('ui.speed');
      const hpLabel = loc.getText('ui.max_hp');
      const cooldownLabel = loc.getText('ui.cooldown');
      const pickupLabel = loc.getText('ui.pickup');

      this.statsText.setText(`${dmgLabel}: ${dmg}%\n${cooldownLabel}: ${cooldown}%\n${spdLabel}: ${spd}\n${pickupLabel}: ${pickup}\n${hpLabel}: ${hp}`);
    }
  }

  public updateInventory(snapshot: UpgradeInventorySnapshot): void {
    this.logger.debug('Inventory update payload', snapshot);

    this.weaponSlots.forEach((slot, index) => {
      const weapon = snapshot.weapons[index];
      if (!weapon) {
        this.updateInventorySlot(slot, null);
        return;
      }

      this.updateInventorySlot(slot, {
        icon: WEAPON_ICONS[weapon.id] ?? weapon.icon ?? weapon.id.slice(0, 2).toUpperCase(),
        level: weapon.level,
        maxLevel: weapon.maxLevel,
        color: weapon.color ?? 0x2f5f9f
      });
    });

    this.passiveSlots.forEach((slot, index) => {
      const passive = snapshot.passives[index];
      if (!passive) {
        this.updateInventorySlot(slot, null);
        return;
      }

      this.updateInventorySlot(slot, {
        icon: passive.icon,
        level: passive.level,
        maxLevel: passive.maxLevel,
        color: passive.color
      });
    });
  }

  /**
   * Обновляет состояние HUD
   */
  override update(time: number, delta: number): void {
    // Обновления могут происходить в других методах при изменении данных
    if (this.bossHealthBar) {
      this.bossHealthBar.update(time, delta);
    }
  }

  public updateWave(index: number): void {
    if(this.wave===index)return;
    this.wave=index;
    this.waveText?.setText(LocalizationManager.getInstance().getText('ui.wave',[index+1]));
  }

  public resize(): void {
    this.positionElements();
    this.bossHealthBar?.resize();
  }


  /**
   * Обновляет отображение здоровья
   * @param current - Текущее здоровье
   * @param max - Максимальное здоровье
   */
  updateHealth(current: number, max: number): void {
    this.healthValue?.setText(`${LocalizationManager.getInstance().getText('ui.health')} ${Math.ceil(current)}/${max}`);
    if (this.healthBar) {
      const fillPercentage = Phaser.Math.Clamp(current / max, 0, 1);
      this.healthBar.clear();

      // Фон полосы здоровья
      this.healthBar.fillStyle(UITheme.panel);
      this.healthBar.fillRect(0, 0, this.barWidth, this.barHeight);

      // Цвет заполнения в зависимости от уровня здоровья
      const healthColor = fillPercentage > 0.5 ? UITheme.accent : fillPercentage > 0.25 ? UITheme.warning : 0xe47c69;
      this.healthBar.fillStyle(healthColor);
      this.healthBar.fillRect(0, 0, this.barWidth * fillPercentage, this.barHeight);

      // Граница
      this.healthBar.lineStyle(2, 0x748d72);
      this.healthBar.strokeRect(0, 0, this.barWidth, this.barHeight);
    }
  }

  /**
   * Обновляет отображение опыта
   * @param current - Текущий опыт
   * @param nextLevel - Опыт, необходимый для следующего уровня
   */
  updateXP(current: number, nextLevel: number): void {
    this.xpValue?.setText(`${LocalizationManager.getInstance().getText('ui.experience')} ${current}/${nextLevel}`);
    if (this.xpBar) {
      const fillPercentage = nextLevel > 0 ? Phaser.Math.Clamp(current / nextLevel, 0, 1) : 0;
      this.xpBar.clear();

      // Фон полосы опыта
      this.xpBar.fillStyle(UITheme.panel);
      this.xpBar.fillRect(0, 0, this.barWidth, this.barHeight);

      // Цвет заполнения полосы опыта
      this.xpBar.fillStyle(0x78c9c0);
      this.xpBar.fillRect(0, 0, this.barWidth * fillPercentage, this.barHeight);

      // Граница
      this.xpBar.lineStyle(2, 0x748d72);
      this.xpBar.strokeRect(0, 0, this.barWidth, this.barHeight);
    }
  }

  /**
   * Обновляет таймер выживания
   * @param seconds - Время в секундах
   */
  updateTimer(seconds: number): void {
    if (this.timerText) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      this.timerText.setText(`${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`);
    }
  }

  /**
   * Создает графический элемент для полосы здоровья
   * @param scene - Сцена Phaser
   */
  private createHealthBar(scene: Phaser.Scene): void {
    this.healthBar = new Phaser.GameObjects.Graphics(scene);
    this.add(this.healthBar);
  }

  /**
   * Создает графический элемент для полосы опыта
   * @param scene - Сцена Phaser
   */
  private createXPBar(scene: Phaser.Scene): void {
    this.xpBar = new Phaser.GameObjects.Graphics(scene);
    this.add(this.xpBar);
  }

  /**
   * Создает текстовый элемент для отображения уровня
   * @param scene - Сцена Phaser
   */
  private createLevelText(scene: Phaser.Scene): void {
    const loc = LocalizationManager.getInstance();
    this.levelText = new Phaser.GameObjects.Text(scene, 0, 0, `${loc.getText('ui.level')} 1`, {
      fontSize: '16px',
      color: UITheme.text,
      fontFamily: UITheme.body
    });
    this.add(this.levelText);
  }

  /**
   * Создает текстовый элемент для отображения таймера
   * @param scene - Сцена Phaser
   */
  private createTimerText(scene: Phaser.Scene): void {
    this.timerText = new Phaser.GameObjects.Text(scene, 0, 0, '00:00', {
      fontSize: '16px',
      color: UITheme.text,
      fontFamily: UITheme.body
    });
    this.add(this.timerText);
  }

  private createStatsText(scene: Phaser.Scene): void {
    this.statsText = new Phaser.GameObjects.Text(scene, 0, 0, '', {
      fontSize: '14px',
      color: UITheme.muted,
      fontFamily: UITheme.body,
      align: 'left'
    });
    this.statsText.setVisible(false);
    this.add(this.statsText);
  }

  private createInventorySlots(scene: Phaser.Scene): void {
    this.inventoryContainer = new Phaser.GameObjects.Container(scene, 0, 0);
    this.add(this.inventoryContainer);

    for (let i = 0; i < 6; i++) {
      const x = i * (this.inventorySlotSize + this.inventorySlotGap);
      const weaponSlot = this.createInventorySlot(scene, x, 0, 0x26344a);
      const passiveSlot = this.createInventorySlot(scene, x, this.inventorySlotSize + this.inventorySlotGap, 0x253f2d);
      this.weaponSlots.push(weaponSlot);
      this.passiveSlots.push(passiveSlot);
    }
  }

  private createInventorySlot(scene: Phaser.Scene, x: number, y: number, color: number): InventorySlotView {
    const container = new Phaser.GameObjects.Container(scene, x, y);
    const bg = new Phaser.GameObjects.Rectangle(scene, 0, 0, this.inventorySlotSize, this.inventorySlotSize, color, 0.7);
    bg.setStrokeStyle(1, 0xffffff, 0.35);

    const iconText = new Phaser.GameObjects.Text(scene, 0, -3, '', {
      fontSize: '10px',
      color: UITheme.text,
      fontFamily: UITheme.body,
      fontStyle: 'bold',
      align: 'center'
    }).setOrigin(0.5);

    const levelText = new Phaser.GameObjects.Text(scene, 0, 13, '', {
      fontSize: '16px',
      color: UITheme.text,
      fontFamily: UITheme.body,
      align: 'center'
    }).setOrigin(0.5);

    const image = new Phaser.GameObjects.Image(scene, 0, -5, 'ui_basic_shot').setDisplaySize(24, 24).setVisible(false);
    container.add([bg, image, iconText, levelText]);
    this.inventoryContainer?.add(container);
    return { bg, iconText, levelText, image };
  }

  private updateInventorySlot(slot: InventorySlotView, item: InventorySlotItem | null): void {
    if (!item) {
      slot.bg.setAlpha(0.28);
      slot.bg.setFillStyle(0x1e1e1e, 0.45);
      slot.image.setVisible(false);
      slot.iconText.setText('');
      slot.levelText.setText('');
      return;
    }

    slot.bg.setAlpha(1);
    slot.bg.setFillStyle(item.color, 0.78);
    const hasIcon = this.scene.textures.exists(item.icon);
    slot.image.setVisible(hasIcon);
    if(hasIcon) slot.image.setTexture(item.icon).setDisplaySize(24,24);
    slot.iconText.setText(hasIcon ? '' : this.formatInventoryIcon(item.icon));
    slot.levelText.setText(item.maxLevel ? `${item.level}/${item.maxLevel}` : `${LocalizationManager.getInstance().getText('ui.level')}${item.level}`);
  }

  private formatInventoryIcon(icon: string): string {
    return icon
      .replace(/^passive_/, '')
      .replace(/_/g, ' ')
      .split(' ')
      .map(part => part.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  }

  private createPauseButton(scene: Phaser.Scene): void {
    this.pauseButton = new Phaser.GameObjects.Container(scene, 0, 0);

    const bg = new Phaser.GameObjects.Rectangle(scene, 0, 0, 44, 44, UITheme.panel, 0.8);
    bg.setStrokeStyle(1.5, 0xffffff, 0.6);

    const icon = new Phaser.GameObjects.Text(scene, 0, 0, '||', {
      fontSize: '14px',
      color: UITheme.text,
      fontFamily: UITheme.body,
      fontStyle: 'bold'
    }).setOrigin(0.5);

    this.pauseButton.add([bg, icon]);
    if(scene.textures.exists('ui_pause')) { icon.setVisible(false); this.pauseButton.add(scene.add.image(0,0,'ui_pause').setDisplaySize(28,28)); }

    bg.setInteractive({ useHandCursor: true });

    bg.on('pointerover', () => {
      bg.setFillStyle(UITheme.hover, 0.8);
      scene.input.setDefaultCursor('pointer');
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(UITheme.panel, 0.8);
      scene.input.setDefaultCursor('default');
    });

    const activate = () => {
      const mainScene = scene as any;
      const isPaused = mainScene.isPaused || false;
      const isLevelUpVisible = mainScene.levelUpScreen?.visible || false;
      const isChestOpenVisible = mainScene.chestOpenScreen?.visible || false;
      const isVictory = mainScene.victoryTriggered || mainScene.victorySceneStarted || false;
      const isGameOver = mainScene.runFinalized || false;

      if (isPaused || isLevelUpVisible || isChestOpenVisible || isVictory || isGameOver) {
        this.logger.debug('Pause button click ignored', {
          isPaused,
          isLevelUpVisible,
          isChestOpenVisible,
          isVictory,
          isGameOver
        });
        return;
      }

      this.logger.info('Pause button clicked');

      if (mainScene.eventBus) {
        mainScene.eventBus.publish({ type: 'game:pause', data: { reason: 'user' } });
      }

      if (mainScene.pauseScreen) {
        mainScene.pauseScreen.show(mainScene.weaponSystem, mainScene.upgradeSystem, mainScene.playerStats);
      }

      scene.input.setDefaultCursor('default');
    };
    bg.on('pointerdown', activate);
    this.eventUnsubscribers.push(bindUIFocus(scene, [{setFocused: value => bg.setStrokeStyle(value ? 3 : 1, UITheme.warning), activate}]));

    this.add(this.pauseButton);
  }

  /**
   * Позиционирует элементы UI
   */
  private positionElements(): void {
    if (this.healthBar) {
      this.healthBar.setPosition(this.barPadding, this.barPadding);
    }

    if (this.xpBar) {
      this.xpBar.setPosition(this.barPadding, this.barPadding * 2 + this.barHeight);
    }

    if (this.levelText) {
      this.levelText.setPosition(this.barPadding, this.barPadding * 3 + this.barHeight * 2);
    }

    if (this.timerText && this.levelText) {
      this.timerText.setPosition(
        110,
        this.barPadding * 3 + this.barHeight * 2
      );
    }

    if (this.statsText && this.timerText) {
      this.statsText.setPosition(
        this.barPadding,
        this.timerText.y + this.timerText.height + 10
      );
    }

    if (this.inventoryContainer) {
      const width = this.scene.scale.width;
      const totalWidth = 6 * this.inventorySlotSize + 5 * this.inventorySlotGap;
      this.inventoryContainer.setPosition(
        width - totalWidth - this.barPadding + this.inventorySlotSize / 2,
        this.barPadding + this.inventorySlotSize / 2
      );

      if (this.pauseButton) {
        const pauseButtonX = (width - totalWidth - this.barPadding) - 12 - 15;
        const pauseButtonY = this.inventoryContainer.y + (this.inventorySlotSize + this.inventorySlotGap) / 2;
        this.pauseButton.setPosition(pauseButtonX, pauseButtonY);
      }
    }
  }

  public dispose(): void {
    if (this.isDisposed) {
      this.logger.warn('dispose:already-disposed');
      return;
    }

    if (!this.isInitialized) {
      this.logger.warn('dispose:called-before-init');
    }

    this.eventUnsubscribers.splice(0).forEach(unsubscribe => unsubscribe());
    this.isDisposed = true;
    this.isInitialized = false;
    this.logger.debug('dispose:complete');
  }

  private bindBusEvent(bus: { on: (type: string, listener: (event: any) => void) => void; off: (type: string, listener: (event: any) => void) => void }, type: string, listener: (event: any) => void): void {
    bus.on(type, listener);
    this.eventUnsubscribers.push(() => bus.off(type, listener));
  }
}

interface InventorySlotView {
  image: Phaser.GameObjects.Image;
  bg: Phaser.GameObjects.Rectangle;
  iconText: Phaser.GameObjects.Text;
  levelText: Phaser.GameObjects.Text;
}

interface InventorySlotItem {
  icon: string;
  level: number;
  maxLevel?: number;
  color: number;
}
